import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { cataloguePreviewService } from "@/api/services";
import type { DocumentResolutionResult } from "@/types/domain";
import { useI18n } from "@/i18n/I18nProvider";

export const PreviewUnavailablePage = () => {
  const { lang } = useI18n();
  return (
    <section data-testid="preview-route-unavailable" className="mx-auto max-w-2xl rounded-sm border border-amber-500/30 bg-card p-8">
      <h1 className="text-xl font-bold">
        {lang === "fr" ? "Module fermé en Preview" : "Module unavailable in Preview"}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {lang === "fr"
          ? "Cette route n'est pas encore autorisée dans la surface Preview en lecture seule."
          : "This route is not yet allowed in the read-only Preview surface."}
      </p>
    </section>
  );
};

export default function PreviewPage() {
  const { lang } = useI18n();
  const [reference, setReference] = useState("");
  const [clientId, setClientId] = useState("");
  const [result, setResult] = useState<DocumentResolutionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await cataloguePreviewService.resolveDocument(reference, clientId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Erreur inattendue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="preview-home">
      <section className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 text-amber-600" />
          <div>
            <h1 className="text-xl font-bold">ROC Preview</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {lang === "fr"
                ? "Environnement isolé en lecture seule. Aucune commande, allocation ou donnée métier ne peut être créée."
                : "Isolated read-only environment. No order, allocation, or business data can be created."}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-sm border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">
          {lang === "fr" ? "Résolution documentaire" : "Document resolution"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {lang === "fr"
            ? "Valide une référence client contre les fixtures synthétiques, sans écriture PostgreSQL."
            : "Resolve a customer reference against synthetic fixtures without PostgreSQL writes."}
        </p>

        <form onSubmit={submit} className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="space-y-1.5 text-sm font-medium">
            {lang === "fr" ? "Identifiant UUID du client synthétique" : "Synthetic customer UUID"}
            <input
              required
              type="text"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full rounded-sm border border-border bg-background px-3 py-2 font-mono text-sm"
              data-testid="preview-client-id"
            />
          </label>
          <label className="space-y-1.5 text-sm font-medium">
            {lang === "fr" ? "Référence documentaire" : "Document reference"}
            <input
              required
              maxLength={200}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
              data-testid="preview-document-reference"
            />
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="ops-focus rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              data-testid="preview-resolve-submit"
            >
              {loading
                ? (lang === "fr" ? "Résolution…" : "Resolving…")
                : (lang === "fr" ? "Résoudre" : "Resolve")}
            </button>
          </div>
        </form>

        {error && (
          <p className="mt-4 rounded-sm border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" data-testid="preview-resolution-error">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-5 rounded-sm border border-border bg-background p-4" data-testid="preview-resolution-result">
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <p><span className="font-semibold">Statut :</span> {result.statut_resolution}</p>
              <p><span className="font-semibold">Référence normalisée :</span> {result.reference_normalisee}</p>
              <p><span className="font-semibold">SKU :</span> {result.sku_rockfall ?? "—"}</p>
              <p><span className="font-semibold">RF Code :</span> {result.rf_code ?? "—"}</p>
              <p><span className="font-semibold">Configuration :</span> {result.statut_configuration ?? "—"}</p>
              <p><span className="font-semibold">Candidats :</span> {result.nombre_candidats}</p>
            </div>
            {result.candidats.length > 0 && (
              <ul className="mt-4 space-y-2">
                {result.candidats.map((candidate) => (
                  <li key={`${candidate.produit_id}:${candidate.configuration_id ?? "produit"}`} className="rounded-sm border border-border p-3 text-sm">
                    <span className="font-semibold">{candidate.sku_rockfall}</span>
                    {candidate.rf_code ? ` · ${candidate.rf_code}` : ""} — {candidate.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
